/**
 * Copyright (c) 2025 Celestis AI
 *
 * WARNING: Unauthorized copying, modification, distribution, or use of this
 * software, via any medium, is strictly prohibited without explicit written
 * permission from Celestis AI.
 *
 * All rights reserved.
 *
 * Environment variable utilities for required configuration.
 *
 * Provides centralized functions for retrieving required environment
 * variables with consistent error handling and fail-fast behavior.
 * Env *names* live in .env.toml; values come from build/.env / Octopus.
 */

const VARSET_TIP =
  '\n💡 Tip: octoctl -p UI -C . doctor\n' +
  '   Get: octoctl -p UI -C . get --env beta\n' +
  '   Persist: octoctl -p UI -C . get --env beta --persist\n';

/**
 * Check if an environment variable value is empty or missing.
 *
 * @param value - The value to check
 * @returns true if the value is empty, null, undefined, or whitespace-only
 */
export function isEmptyEnvValue(value: unknown): boolean {
  return value === undefined ||
         value === null ||
         value === '' ||
         value === 'undefined' ||
         (typeof value === 'string' && value.trim() === '');
}

/**
 * Get a required environment variable (string).
 *
 * Throws Error with a clear error message if the variable is not set.
 *
 * @param varName - Name of the environment variable (must start with VITE_)
 * @param description - Optional description of what the variable is used for
 * @returns The value of the environment variable (never empty/undefined)
 * @throws Error if the environment variable is not set or is empty
 */
export function getRequiredEnv(varName: string, description?: string): string {
  const value = import.meta.env[varName];

  if (isEmptyEnvValue(value)) {
    let errorMsg = `${varName} environment variable is not set. `;
    errorMsg +=
      'Please set it via Octopus fetch (.env.octopus) and/or .env overrides, then rebuild.';

    if (description) {
      errorMsg += ` (${description})`;
    }

    throw new Error(errorMsg + VARSET_TIP);
  }

  return String(value);
}

/**
 * Get a required environment variable as an integer.
 *
 * Throws Error if the variable is not set or cannot be converted to int.
 *
 * @param varName - Name of the environment variable
 * @param description - Optional description of what the variable is used for
 * @returns The integer value of the environment variable
 * @throws Error if the environment variable is not set or cannot be converted to int
 */
export function getRequiredIntEnv(varName: string, description?: string): number {
  const valueStr = getRequiredEnv(varName, description);
  const parsed = Number.parseInt(valueStr, 10);

  if (Number.isNaN(parsed)) {
    throw new Error(
      `${varName} environment variable must be a valid integer. ` +
      `Got: ${valueStr}` +
      VARSET_TIP
    );
  }

  return parsed;
}

/**
 * Get an optional environment variable (string).
 *
 * Returns undefined or the default value if the variable is not set.
 *
 * @param varName - Name of the environment variable
 * @param defaultVal - Optional default value to return if variable is not set (defaults to undefined)
 * @returns The value of the environment variable, or default/undefined if not set
 */
export function getOptionalEnv(varName: string, defaultVal?: string): string | undefined {
  const value = import.meta.env[varName];

  if (isEmptyEnvValue(value)) {
    return defaultVal;
  }

  return String(value);
}

/**
 * Get an optional environment variable as an integer.
 *
 * Returns undefined or the default value if the variable is not set.
 *
 * @param varName - Name of the environment variable
 * @param defaultVal - Optional default value to return if variable is not set (defaults to undefined)
 * @returns The integer value of the environment variable, or default/undefined if not set
 * @throws Error if the variable is set but cannot be converted to int
 */
export function getOptionalIntEnv(varName: string, defaultVal?: number): number | undefined {
  const value = import.meta.env[varName];

  if (isEmptyEnvValue(value)) {
    return defaultVal;
  }

  const parsed = Number.parseInt(String(value), 10);
  if (Number.isNaN(parsed)) {
    throw new Error(
      `${varName} environment variable must be a valid integer. ` +
      `Got: ${value}` +
      VARSET_TIP
    );
  }

  return parsed;
}

/**
 * Get an optional boolean environment variable.
 *
 * Returns the default value if the variable is not set.
 * Treats 'true', '1', 'yes' as true, everything else as false.
 *
 * @param varName - Name of the environment variable
 * @param defaultVal - Default value to return if variable is not set (defaults to false)
 * @returns The boolean value of the environment variable, or default if not set
 */
export function getOptionalBoolEnv(varName: string, defaultVal: boolean = false): boolean {
  const value = import.meta.env[varName];

  if (isEmptyEnvValue(value)) {
    return defaultVal;
  }

  const strValue = String(value).toLowerCase().trim();
  return strValue === 'true' || strValue === '1' || strValue === 'yes';
}

/**
 * Get an environment variable by dynamic name (for cases like VITE_STRIPE_PRODUCT_CREDIT_100).
 * Prefer getRequiredEnv / getOptionalEnv when the name is static.
 *
 * @param varName - Name of the environment variable
 * @returns The value of the environment variable, or undefined if not set
 */
export function getDynamicEnv(varName: string): string | undefined {
  const value = import.meta.env[varName];
  if (isEmptyEnvValue(value)) {
    return undefined;
  }
  return String(value);
}
